import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

export default class extends Controller {
  static targets = [
    "nameInput",
    "genderSelect", 
    "defaultQuantityInput",
    "maxQuantityInput",
    "valueInput",
    "addedResourcesList",
    "addedResourcesItems",
    "validationError",
    "noResourcesMessage"
  ]
  static values = {
    existingResources: Array
  }
  private addedResources: Array<{
    index: number
    name: string
    gender: string
    default_quantity: string
    max_quantity: string | null
    in_kind_value: string | null
  }> = []
  private resourceIndex = 0

  connect() {
    this.initializeWithExistingResources()
  }

  addResourceToList() {
    const name = this.nameInputTarget.value.trim()
    const gender = this.genderSelectTarget.value
    const defaultQuantity = parseInt(this.defaultQuantityInputTarget.value) || 0
    const maxQuantity = this.maxQuantityInputTarget.value ? parseInt(this.maxQuantityInputTarget.value) : null
    const value = this.valueInputTarget.value

    // Clear previous validation errors
    this.clearValidationErrors()

    // Validate required fields
    if (!name || !defaultQuantity) {
      this.showValidationError(i18n.t('resource_items_form.required_fields_error'))
      return
    }

    // Validate max quantity if provided
    if (maxQuantity && maxQuantity < defaultQuantity) {
      this.showValidationError(i18n.t('resource_items_form.max_quantity_error'))
      return
    }

    // Create resource object
    const resource = {
      index: this.resourceIndex++,
      name: name,
      gender: gender,
      default_quantity: defaultQuantity,
      max_quantity: maxQuantity || null,
      in_kind_value: value || null
    }

    // Add to list
    this.addedResources.push(resource)
    
    // Update the form with hidden inputs
    this.updateHiddenInputs()
    
    // Update the visual display
    this.updateVisualDisplay()
    
    // Clear form
    this.clearResourceForm()
  }

  removeResourceFromList(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const index = parseInt(button.dataset.resourceItemsFormIndexParam || '0')
    
    // Remove from array
    this.addedResources = this.addedResources.filter(r => r.index !== index)
    
    // Update hidden inputs
    this.updateHiddenInputs()
    
    // Update visual display
    this.updateVisualDisplay()
  }

  clearResourceForm() {
    this.nameInputTarget.value = ''
    this.genderSelectTarget.value = 'Non-Specific'
    this.defaultQuantityInputTarget.value = '1'
    this.maxQuantityInputTarget.value = ''
    this.valueInputTarget.value = ''
    this.clearValidationErrors()
  }

  private showValidationError(message: string) {
    this.validationErrorTarget.textContent = message
    this.validationErrorTarget.style.display = 'block'
  }

  private clearValidationErrors() {
    this.validationErrorTarget.style.display = 'none'
    this.validationErrorTarget.textContent = ''
  }

  private addToVisualList(resource: typeof this.addedResources[0]) {
    // Create list item
    const listItem = document.createElement('div')
    listItem.className = 'card bg-base-100 shadow-sm border border-gray-200'
    listItem.innerHTML = `
      <div class="card-body p-4">
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <h3 class="text-sm font-semibold text-gray-900 mb-1">${resource.name}</h3>
            <p class="text-xs text-gray-600">${resource.gender} • ${i18n.t('resource_items_form.default_qty', { qty: resource.default_quantity })}${resource.max_quantity ? ` • ${i18n.t('resource_items_form.max_qty', { qty: resource.max_quantity })}` : ''}</p>
          </div>
          <button type="button"
                  class="btn btn-ghost btn-xs text-red-600 hover:bg-red-50 ml-3 flex-shrink-0"
                  data-action="click->resource-items-form#removeResourceFromList"
                  data-resource-items-form-index-param="${resource.index}"
                  title="${i18n.t('resource_items_form.remove_resource')}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `
    
    this.addedResourcesItemsTarget.appendChild(listItem)
  }

  private updateVisualDisplay() {
    // Clear current items
    this.addedResourcesItemsTarget.innerHTML = ''
    
    if (this.addedResources.length === 0) {
      this.addedResourcesListTarget.style.display = 'none'
      this.noResourcesMessageTarget.style.display = 'block'
      return
    }
    
    // Show the list and hide the "no resources" message
    this.addedResourcesListTarget.style.display = 'block'
    this.noResourcesMessageTarget.style.display = 'none'
    
    // Rebuild list
    this.addedResources.forEach(resource => {
      this.addToVisualList(resource)
    })
  }

  private updateHiddenInputs() {
    // Remove existing hidden inputs (only the ones we created, not the form inputs)
    const existingHiddenInputs = this.element.querySelectorAll('input[type="hidden"][name*="[new_resources]"]')
    existingHiddenInputs.forEach(input => input.remove())
    
    // Add new hidden inputs for each resource
    this.addedResources.forEach((resource, index) => {
      // Create hidden inputs for this resource
      const fields = [
        { name: 'name', value: resource.name },
        { name: 'gender', value: resource.gender },
        { name: 'default_quantity', value: resource.default_quantity },
        { name: 'max_quantity', value: resource.max_quantity },
        { name: 'in_kind_value', value: resource.in_kind_value }
      ]
      
      fields.forEach(field => {
        if (field.value !== null && field.value !== '') {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = `new_resources[${index}][${field.name}]`
          input.value = field.value
          this.element.appendChild(input)
        }
      })
    })
  }

  private initializeWithExistingResources() {
    if (this.existingResourcesValue.length > 0) {
      // Add existing resources to the list
      this.existingResourcesValue.forEach((resourceData: any) => {
        const resource = {
          index: this.resourceIndex++,
          name: resourceData.name,
          gender: resourceData.gender,
          default_quantity: resourceData.default_quantity,
          max_quantity: resourceData.max_quantity,
          in_kind_value: resourceData.in_kind_value
        }
        this.addedResources.push(resource)
      })

      // Update the visual display and hidden inputs
      this.updateHiddenInputs()
      this.updateVisualDisplay()
    }
  }
}
